import { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Breadcrumb, Button, Dropdown, Icon, Menu, Popup, Table, TextArea } from 'semantic-ui-react';
import PageContainer from '../../common/PageContainer';
import * as selectors from '../../../redux/selectors';
import * as actions from '../../../redux/actions';
import { CSVLink } from 'react-csv';
import { Formik } from 'formik';
import InvoiceSearchForm from './InvoiceSearchForm';
import { SelectField, TextField } from '../../common/FormFields';
import { DateInput } from 'semantic-ui-calendar-react';
import * as moment from 'moment';

const ReviewInvoicingPage = ({
  invoices,
  updateInvoice,
  getUsers,
  sites,
  users,
  editors,
  getSites,
  searchInvoices,
}) => {
  useEffect(() => {
    searchInvoices({ month: moment().month(), year: moment().year() });
  }, []);

  const toRecord = invoice => ({
    Contributor: invoice?.contributor?.name,
    Editor: invoice?.editor?.name,
    Date: invoice?.date,
    Month: moment(invoice.date, 'YYYY-MM-DD').format('MMMM'),
    Year: invoice?.year,
    Amount: invoice?.amount,
    Description: invoice?.description,
    Site: `${invoice?.site?.name}`,
    'Is Approved': invoice?.isApproved ? 'Yes' : 'No',
    'Editor Comments': invoice?.editorComments,
  });

  const handleSubmit = ({ invoices }) => invoices.forEach(invoice => updateInvoice(invoice));

  const types = ['Article'].map(k => ({
    key: k,
    text: k,
    value: k,
  }));

  useEffect(() => {
    const currentYear = moment().year();
    const startYear = currentYear - 5;

    const temp = [''];

    for (let index = 0; index < 6; index++) {
      temp.push(startYear + index);
    }
  }, []);

  const getPage = () => (
    <Formik initialValues={{ invoices }} onSubmit={handleSubmit} enableReinitialize>
      {formik => (
        <PageContainer>
          <Breadcrumb>
            <Breadcrumb.Section>Invoices</Breadcrumb.Section>
            <Breadcrumb.Divider icon="right angle" />
            <Breadcrumb.Section>Review Invoices</Breadcrumb.Section>
          </Breadcrumb>

          <Menu borderless>
            <Menu.Item header>
              <Icon name="user circle outline" />
              Review Invoices
            </Menu.Item>

            <Menu.Menu position="right">
              <Menu.Item>
                <Button positive content="Save" icon="save" onClick={() => formik.handleSubmit()} />
              </Menu.Item>
              <Menu.Item>
                <CSVLink data={invoices.filter(record => record.isApproved).map(toRecord)} filename={'invoices.csv'}>
                  <Button content="Export" icon="download" />
                </CSVLink>
              </Menu.Item>

              <Menu.Item header>
                <Popup
                  className="adslot-search-popup"
                  wide
                  flowing
                  position="bottom right"
                  trigger={<Button content="Filter" fluid icon="filter" />}
                  on="click"
                >
                  <InvoiceSearchForm />
                </Popup>
              </Menu.Item>
            </Menu.Menu>
          </Menu>

          <Table size="small" compact="very" className="review-invoices-table">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Contributor</Table.HeaderCell>
                <Table.HeaderCell>Description</Table.HeaderCell>
                <Table.HeaderCell>Assigned By</Table.HeaderCell>
                <Table.HeaderCell>Completed</Table.HeaderCell>
                <Table.HeaderCell>Amount</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>City</Table.HeaderCell>
                <Table.HeaderCell>Approved</Table.HeaderCell>
                <Table.HeaderCell>Comments from Editor</Table.HeaderCell>
                <Table.HeaderCell></Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {formik.values.invoices
                .filter(i => !i.isDeleted)
                .map((invoice, idx) => (
                  <Table.Row key={idx}>
                    <Table.Cell width="2">
                      <SelectField
                        name={`invoices[${idx}].contributorId`}
                        options={users}
                        onChange={formik.handleChange}
                        value={invoice.contributorId}
                        formik={formik}
                      />
                    </Table.Cell>
                    <Table.Cell width="3">
                      <TextArea
                        name={`invoices[${idx}].description`}
                        formik={formik}
                        style={{ border: `1px solid gainsboro`, width: '100%' }}
                        value={invoice.description || ''}
                        onChange={formik.handleChange}
                        error={formik.touched.description && Boolean(formik.errors.description)}
                        errorMessage={formik.errors.description}
                      />
                    </Table.Cell>
                    <Table.Cell width="2">
                      <SelectField
                        name={`invoices[${idx}].editorId`}
                        options={editors}
                        onChange={formik.handleChange}
                        value={invoice.editorId}
                        formik={formik}
                      />
                    </Table.Cell>
                    <Table.Cell width="1">
                      <DateInput
                        name="date"
                        closable={true}
                        placeholder="Date completed"
                        popupPosition="bottom center"
                        iconPosition="left"
                        dateFormat="YYYY-MM-DD"
                        value={invoice.date}
                        onChange={(e, { value }) => formik.setFieldValue(`invoices[${idx}].date`, value)}
                      />
                    </Table.Cell>
                    <Table.Cell width="1">
                      <TextField
                        name={`invoices[${idx}].amount`}
                        formik={formik}
                        value={invoice.amount || '0'}
                        onChange={formik.handleChange}
                        error={formik.touched.amount && Boolean(formik.errors.amount)}
                        errorMessage={formik.errors.amount}
                      />
                    </Table.Cell>
                    <Table.Cell width="1">
                      <Dropdown
                        fluid
                        search
                        clearable
                        selection
                        options={types}
                        id="type"
                        data-testid="type"
                        label="Type"
                        name="type"
                        value={invoice.type}
                        onChange={(e, d) => formik.setFieldValue(`invoices[${idx}].type`, d.value)}
                      />
                    </Table.Cell>
                    <Table.Cell width="2">
                      <SelectField
                        name={`invoices[${idx}].siteId`}
                        options={sites}
                        onChange={formik.handleChange}
                        value={invoice.siteId}
                        formik={formik}
                      />
                    </Table.Cell>
                    <Table.Cell width="1">
                      <input
                        type="checkbox"
                        checked={invoice.isApproved}
                        onChange={({ target: { checked } }) =>
                          formik.setFieldValue(`invoices[${idx}].isApproved`, checked)
                        }
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <TextField
                        name={`invoices[${idx}].editorComments`}
                        formik={formik}
                        value={invoice.editorComments || ''}
                        onChange={formik.handleChange}
                        error={formik.touched.editorComments && Boolean(formik.errors.editorComments)}
                        errorMessage={formik.errors.editorComments}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Popup
                        trigger={<Button negative icon="trash" basic size="tiny" />}
                        content={
                          <Button
                            color="green"
                            content="Are you sure?"
                            onClick={() => updateInvoice({ ...formik.values.invoices[idx], isDeleted: true })}
                          />
                        }
                        on="click"
                        position="bottom left"
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
          </Table>
        </PageContainer>
      )}
    </Formik>
  );

  return <>{invoices && getPage()}</>;
};

const mapStateToProps = state => ({
  invoices: selectors.getAllInvoices(state),
  user: selectors.getUser(state),
  users: selectors.getUserOptions(state),
  editors: selectors.getEditors(state),
  sites: selectors.admin.siteOptions(state),
});

const mapDispatchToProps = dispatch => ({
  getInvoices: bindActionCreators(actions.invoices.getInvoices, dispatch),
  getUsers: bindActionCreators(actions.admin.getUsers, dispatch),
  updateInvoice: bindActionCreators(actions.invoices.updateInvoice, dispatch),
  getSites: bindActionCreators(actions.admin.getSites, dispatch),
  searchInvoices: bindActionCreators(actions.invoices.searchInvoices, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ReviewInvoicingPage);
